<template>
  <div class="tool-page wm-page">
    <div class="tool-content">
      <div class="tool-heading">
        <div>
          <h2>图片去水印</h2>
          <p>上传图片，涂抹要去掉的水印 / 文字区域，自动擦除并保留背景</p>
        </div>
        <app-icon class="app-icon" name="broken_image"></app-icon>
      </div>

      <div class="tool-card">
        <!-- 1. 选图 -->
        <section class="section-block" v-if="!imageUrl">
          <div class="section-title">选择图片</div>
          <div class="wm-drop" @click="pickImage" @dragover.prevent @drop.prevent="onDrop">
            <i class="el-icon-plus wm-drop-icon"></i>
            <p class="wm-drop-text">点击选择，或把图片拖到这里</p>
            <p class="wm-drop-tip">支持 JPG / PNG / WebP，单张不超过 5MB</p>
          </div>
        </section>

        <!-- 2. 涂抹 -->
        <section class="section-block" v-else>
          <div class="section-header">
            <span class="section-title">
              涂抹要去掉的区域
              <span v-if="processed" class="wm-badge">已处理</span>
            </span>
            <div class="wm-head-actions">
              <el-button size="mini" plain icon="el-icon-refresh-left" :disabled="!strokes.length"
                @click="undoStroke">撤销涂抹</el-button>
              <el-button size="mini" plain icon="el-icon-delete" :disabled="!strokes.length"
                @click="clearStrokes">清空涂抹</el-button>
              <el-button size="mini" plain @click="pickImage">换一张</el-button>
            </div>
          </div>

          <div class="wm-stage" ref="stage">
            <img ref="baseImg" class="wm-img" :src="imageUrl" alt="待处理图片" @load="onImageLoad">
            <canvas ref="maskCanvas" class="wm-canvas" @pointerdown="onPaintDown" @pointermove="onPaintMove"
              @pointerup="onPaintUp" @pointercancel="onPaintUp" @pointerleave="onPaintUp"></canvas>
          </div>

          <div class="wm-tools">
            <span class="wm-tools-label">画笔大小</span>
            <el-slider v-model="brushSize" :min="8" :max="90" :step="2" class="wm-slider"></el-slider>
            <span class="wm-tools-value">{{ brushSize }}px</span>
          </div>
          <p class="wm-tip">涂抹处显示为半透明红色，代表这部分会被擦除；可以反复涂抹叠加，也可以撤销。</p>
        </section>

        <el-button class="submit-btn" type="primary" :loading="processing" :disabled="!canSubmit" @click="submit">
          {{ processing ? processingText : '开始处理' }}
        </el-button>

        <!-- 处理结果直接替换上面那张图，这里只做版本与下载操作 -->
        <div class="wm-actions" v-if="imageUrl">
          <div class="wm-versions">
            <button type="button" class="wm-version-btn" :disabled="!canUndoProcess" @click="undoProcess">
              <i class="el-icon-refresh-left"></i>
              <span>撤销处理</span>
            </button>
            <span class="wm-version-sep"></span>
            <button type="button" class="wm-version-btn" :disabled="!canRedoProcess" @click="redoProcess">
              <i class="el-icon-refresh-right"></i>
              <span>恢复处理</span>
            </button>
          </div>
          <button type="button" class="wm-download" @click="downloadCurrent">
            <i class="el-icon-download"></i>
            <span>下载当前图</span>
          </button>
        </div>
      </div>

      <!-- 文件选择框放在两个区之外：选中图片后「换一张」也要能再次唤起它（放进 v-if 里会随选图区一起被销毁） -->
      <input ref="file" type="file" accept="image/jpeg,image/png,image/webp" class="wm-file" @change="onFileChange">
    </div>
  </div>
</template>

<script>
const COS = require('cos-js-sdk-v5')

const MAX_SIZE = 5 * 1024 * 1024
const POLL_INTERVAL = 3000
const POLL_TIMEOUT = 120000

export default {
  name: 'User2Watermark',
  data() {
    return {
      imageUrl: '',        // 当前底图（本地 objectURL 或远端图，处理结果会替换它）
      imageFile: null,     // 本地待上传的文件（底图来自远端时为 null）
      cosUrl: '',          // 底图在 COS 上的地址（处理结果本身就是远端地址）
      natural: { width: 0, height: 0 },
      display: { width: 0, height: 0 },
      strokes: [],
      redoStrokes: [],
      brushSize: 28,
      painting: false,
      processing: false,
      processingText: '开始处理',
      // 图像版本栈（对齐小程序：imageHistory / imageRedoStack）
      history: [],
      redoStack: [],
      processed: false,
      isMobile: false
    }
  },
  computed: {
    canSubmit() {
      return !!this.imageUrl && this.strokes.length > 0 && !this.processing
    },
    // 栈里超过 1 版才能撤销（对齐小程序 canUndoImage）
    canUndoProcess() {
      return this.history.length > 1 && !this.processing
    },
    canRedoProcess() {
      return this.redoStack.length > 0 && !this.processing
    }
  },
  methods: {
    // ── 选图 ──
    pickImage() {
      if (this.$refs.file) this.$refs.file.click()
    },
    onFileChange(e) {
      const files = e.target.files || []
      if (files.length) this.loadFile(files[0])
      e.target.value = ''
    },
    onDrop(e) {
      const files = (e.dataTransfer && e.dataTransfer.files) || []
      if (files.length) this.loadFile(files[0])
    },
    loadFile(file) {
      if (!file.type || file.type.indexOf('image/') !== 0) {
        this.$msg.error('只能上传图片文件')
        return
      }
      if (file.size > MAX_SIZE) {
        this.$msg.error('图片不能超过 5MB')
        return
      }
      if (this.imageUrl && this.imageUrl.indexOf('blob:') === 0) {
        URL.revokeObjectURL(this.imageUrl)
      }
      this.imageFile = file
      this.cosUrl = ''
      this.imageUrl = URL.createObjectURL(file)
      this.strokes = []
      this.redoStrokes = []
      this.history = []
      this.redoStack = []
      this.processed = false
    },
    // 把图片按可用区域等比适配：小图也放大显示（限幅，避免糊得离谱）
    fitImage() {
      const img = this.$refs.baseImg
      const stage = this.$refs.stage
      if (!img || !stage) return null
      const nw = img.naturalWidth || 0
      const nh = img.naturalHeight || 0
      if (!nw || !nh) return null
      const boxW = Math.max(160, stage.clientWidth - 20)
      const boxH = Math.max(200, Math.min(window.innerHeight * 0.56, 720))
      let scale = Math.min(boxW / nw, boxH / nh)
      scale = Math.min(scale, 4)
      img.style.width = Math.round(nw * scale) + 'px'
      img.style.height = Math.round(nh * scale) + 'px'
      return { width: Math.round(nw * scale), height: Math.round(nh * scale) }
    },
    onImageLoad() {
      const img = this.$refs.baseImg
      const canvas = this.$refs.maskCanvas
      if (!img || !canvas) return
      const size = this.fitImage()
      if (!size) {
        // 图片刚加载完可能还没布局完成，下一帧再量一次
        window.requestAnimationFrame(() => this.onImageLoad())
        return
      }
      this.natural = { width: img.naturalWidth || 0, height: img.naturalHeight || 0 }
      this.display = { width: size.width, height: size.height }
      const dpr = window.devicePixelRatio || 1
      // 位图按 dpr 放大保证清晰，CSS 尺寸必须显式设成显示尺寸，才能和图片完全重合
      canvas.width = Math.round(size.width * dpr)
      canvas.height = Math.round(size.height * dpr)
      canvas.style.width = size.width + 'px'
      canvas.style.height = size.height + 'px'
      const ctx = canvas.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      this.redraw()
    },
    // ── 涂抹 ──
    onPaintDown(e) {
      if (!this.imageUrl) return
      const canvas = this.$refs.maskCanvas
      const rect = canvas.getBoundingClientRect()
      const point = { x: e.clientX - rect.left, y: e.clientY - rect.top }
      this.painting = true
      this.redoStrokes = []
      this.strokes.push({ size: this.brushSize, points: [point] })
      this.redraw()
      if (canvas.setPointerCapture && e.pointerId != null) {
        try { canvas.setPointerCapture(e.pointerId) } catch (err) { /* ignore */ }
      }
      e.preventDefault()
    },
    onPaintMove(e) {
      if (!this.painting || !this.strokes.length) return
      const canvas = this.$refs.maskCanvas
      const rect = canvas.getBoundingClientRect()
      const stroke = this.strokes[this.strokes.length - 1]
      stroke.points.push({ x: e.clientX - rect.left, y: e.clientY - rect.top })
      this.redraw()
      e.preventDefault()
    },
    onPaintUp() {
      this.painting = false
    },
    undoStroke() {
      if (!this.strokes.length) return
      this.redoStrokes.push(this.strokes.pop())
      this.redraw()
    },
    clearStrokes() {
      this.strokes = []
      this.redoStrokes = []
      this.redraw()
    },
    drawStroke(ctx, stroke, offsetX, offsetY) {
      const points = stroke.points || []
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.lineWidth = stroke.size
      ctx.strokeStyle = 'rgba(239, 68, 68, .45)'
      if (points.length === 1) {
        ctx.beginPath()
        ctx.arc(points[0].x + offsetX, points[0].y + offsetY, stroke.size / 2, 0, Math.PI * 2)
        ctx.fillStyle = 'rgba(239, 68, 68, .45)'
        ctx.fill()
        return
      }
      ctx.beginPath()
      ctx.moveTo(points[0].x + offsetX, points[0].y + offsetY)
      for (let i = 1; i < points.length; i++) {
        ctx.lineTo(points[i].x + offsetX, points[i].y + offsetY)
      }
      ctx.stroke()
    },
    redraw() {
      const canvas = this.$refs.maskCanvas
      if (!canvas) return
      const w = this.display.width
      const h = this.display.height
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, w, h)
      for (let i = 0; i < this.strokes.length; i++) {
        this.drawStroke(ctx, this.strokes[i], 0, 0)
      }
    },
    // 导出遮罩：与原图同尺寸，黑底 + 白色笔迹（后端按白色区域擦除）
    exportMask() {
      if (!this.strokes.length || !this.natural.width) return ''
      const canvas = document.createElement('canvas')
      canvas.width = this.natural.width
      canvas.height = this.natural.height
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#000000'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      const scaleX = this.natural.width / this.display.width
      const scaleY = this.natural.height / this.display.height
      const lineScale = (scaleX + scaleY) / 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = '#ffffff'
      ctx.fillStyle = '#ffffff'
      for (let i = 0; i < this.strokes.length; i++) {
        const stroke = this.strokes[i]
        const points = (stroke.points || []).map(p => ({ x: p.x * scaleX, y: p.y * scaleY }))
        ctx.lineWidth = Math.max(1, stroke.size * lineScale)
        if (points.length === 1) {
          ctx.beginPath()
          ctx.arc(points[0].x, points[0].y, ctx.lineWidth / 2, 0, Math.PI * 2)
          ctx.fill()
          continue
        }
        ctx.beginPath()
        ctx.moveTo(points[0].x, points[0].y)
        for (let j = 1; j < points.length; j++) ctx.lineTo(points[j].x, points[j].y)
        ctx.stroke()
      }
      const dataUrl = canvas.toDataURL('image/png')
      return dataUrl.indexOf(',') > -1 ? dataUrl.split(',')[1] : ''
    },
    // ── 上传原图（新后端发凭证 + COS SDK 直传）──
    uploadImage() {
      const file = this.imageFile
      if (!file) return Promise.resolve(this.cosUrl)
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const hash = Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
      return this.$ajax.post('api/v1/users/upload_dz_cos', {
        type: file.type,
        filename: file.name,
        hash: hash,
        ext: ext,
        size: file.size,
        upload_class: 'temp'
      }, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }).then(res => {
        const body = res.data || {}
        if (body.status !== 200 || !body.data) {
          throw new Error(body.msg || '获取上传凭证失败')
        }
        const cfg = body.data
        const cos = new COS({
          SecretId: cfg.Credentials.TmpSecretId,
          SecretKey: cfg.Credentials.TmpSecretKey,
          SecurityToken: cfg.Credentials.Token,
          StartTime: cfg.startTime,
          ExpiredTime: cfg.ExpiredTime
        })
        return new Promise((resolve, reject) => {
          cos.putObject({
            Bucket: cfg.bucket,
            Region: cfg.region,
            Key: cfg.dz,
            Body: file
          }, (err) => {
            if (err) reject(new Error('图片上传失败'))
            else resolve(cfg.down_dz)
          })
        })
      }).then(url => {
        this.cosUrl = url
        return url
      })
    },
    // ── 提交与轮询 ──
    submit() {
      if (this.processing) return
      const mask = this.exportMask()
      if (!mask) {
        this.$msg.error('请先涂抹要去掉的区域')
        return
      }
      this.processing = true
      this.processingText = '上传图片中…'
      this.uploadImage().then(imageUrl => {
        // 处理前先把「当前底图」压栈（对齐小程序 ensureHistoryBase）
        if (!this.history.length) {
          this.history.push({ imageUrl: this.imageUrl, cosUrl: imageUrl, file: this.imageFile })
        }
        this.processingText = '处理中…'
        return this.$ajax.post('api/v1/users/watermark/image', { image: imageUrl, mask: mask })
      }).then(res => {
        const body = res.data || {}
        if (body.status !== 200 || !body.taskId) {
          throw new Error(body.msg || '任务提交失败')
        }
        return this.pollTask(body.taskId)
      }).then(result => {
        // 结果直接替换当前底图，清空 redo 栈（对齐小程序处理成功后的行为）
        this.history.push({ imageUrl: result, cosUrl: result, file: null })
        this.redoStack = []
        this.processed = true
        this.applyImage(result, result)
        this.$msg.success('处理完成，已替换为新图')
        this.processing = false
        this.processingText = '开始处理'
      }).catch(err => {
        this.$msg.error((err && err.message) || '处理失败，请重试')
        this.processing = false
        this.processingText = '开始处理'
      })
    },
    // 切换当前底图（涂抹状态一并清掉，并让画布按新图重新适配）
    applyImage(imageUrl, cosUrl) {
      this.imageUrl = imageUrl
      this.cosUrl = cosUrl || ''
      this.imageFile = null
      this.strokes = []
      this.redoStrokes = []
      this.$nextTick(() => {
        const img = this.$refs.baseImg
        if (img && img.complete) this.onImageLoad()
      })
    },
    // 撤销处理：退回上一版；恢复处理：再前进一版（与小程序一致）
    undoProcess() {
      if (this.history.length <= 1) return
      const current = this.history.pop()
      if (current) this.redoStack.push(current)
      const prev = this.history[this.history.length - 1]
      if (!prev) return
      this.processed = this.history.length > 1
      this.applyImage(prev.imageUrl, prev.cosUrl)
    },
    redoProcess() {
      if (!this.redoStack.length) return
      const next = this.redoStack.pop()
      this.history.push(next)
      this.processed = this.history.length > 1
      this.applyImage(next.imageUrl, next.cosUrl)
    },
    pollTask(taskId) {
      const started = Date.now()
      const tick = () => {
        if (Date.now() - started > POLL_TIMEOUT) {
          return Promise.reject(new Error('处理超时，请稍后在制作记录里查看或重试'))
        }
        return this.sleep(POLL_INTERVAL).then(() => {
          return this.$ajax.post('api/v1/users/watermark/find', { taskId: taskId })
        }).then(res => {
          const body = res.data || {}
          const data = body.data || {}
          if (body.status !== 200) {
            throw new Error(body.msg || '任务查询失败')
          }
          if (Number(data.status) === 1) {
            if (!data.result) throw new Error('处理成功但没有返回图片地址')
            return data.result
          }
          if (Number(data.status) === 2) {
            throw new Error('处理失败，请调整涂抹区域后重试')
          }
          return tick()
        })
      }
      return tick()
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    // ── 下载当前图（处理结果就是当前底图）──
    downloadCurrent() {
      const url = this.cosUrl || this.imageUrl
      if (!url) {
        this.$msg.error('还没有可下载的图片')
        return
      }
      const name = '去水印_' + Date.now() + '.jpg'
      fetch(url).then(res => res.blob()).then(blob => {
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(objectUrl), 3000)
      }).catch(() => {
        // 跨域拿不到 blob 时退化为新窗口打开，用户可自行另存
        window.open(url, '_blank')
      })
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 900
    }
  },
  mounted() {
    this.updateViewport()
    window.addEventListener('resize', this.updateViewport)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport)
    if (this.imageUrl && this.imageUrl.indexOf('blob:') === 0) URL.revokeObjectURL(this.imageUrl)
  }
}
</script>

<style scoped>
.tool-page {
  min-height: 100%;
  padding: 24px 24px 48px;
  background: #f5f7fa;
  box-sizing: border-box;
}

.tool-content {
  max-width: 1080px;
  margin: 0 auto;
}

.tool-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.tool-heading h2 {
  margin: 0 0 6px;
  color: #1e293b;
  font-size: 22px;
}

.tool-heading p {
  margin: 0;
  color: #94a3b8;
  font-size: 13px;
}

.tool-heading .app-icon {
  padding: 11px;
  border-radius: 12px;
  background: #e8f1fe;
  color: #1a73e8;
  font-size: 28px;
}

.tool-card {
  padding: 24px 24px 40px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, .04);
}

.section-block + .section-block {
  margin-top: 26px;
}

.section-title {
  margin-bottom: 12px;
  color: #1e293b;
  font-size: 14px;
  font-weight: 600;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.section-header .section-title {
  margin-bottom: 0;
}

.wm-head-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.wm-head-actions .el-button {
  margin-left: 0;
}

/* 选图区 */
.wm-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 42px 16px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  background: #f8fafc;
  cursor: pointer;
  transition: all .2s;
}

.wm-drop:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.wm-drop-icon {
  color: #94a3b8;
  font-size: 30px;
}

.wm-drop-text {
  margin: 0;
  color: #475569;
  font-size: 14px;
}

.wm-drop-tip {
  margin: 0;
  color: #94a3b8;
  font-size: 12px;
}

.wm-file {
  display: none;
}

/* 涂抹区 */
.wm-stage {
  position: relative;
  display: flex;
  justify-content: center;
  padding: 10px;
  border-radius: 12px;
  background: #0f172a;
  overflow: hidden;
}

.wm-img {
  display: block;
  /* 尺寸由 fitImage() 按可用区域算出并显式设置（小图也放大），这里不要写死 max-height */
  border-radius: 6px;
}

.wm-canvas {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  cursor: crosshair;
  touch-action: none;
}

.wm-tools {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
}

.wm-tools-label {
  flex: none;
  color: #475569;
  font-size: 13px;
}

.wm-slider {
  flex: 1;
  min-width: 0;
}

.wm-tools-value {
  flex: none;
  color: #94a3b8;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.wm-tip {
  margin: 10px 0 0;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.7;
}

.submit-btn {
  width: 100%;
  margin-top: 26px;
  border-color: #1a73e8;
  background: #1a73e8;
}

/* 版本与下载操作 */
.wm-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}

/* 撤销/恢复做成一体化分段控件，比两个孤立按钮清楚 */
.wm-versions {
  display: flex;
  align-items: stretch;
  border: 1px solid #dbe3ec;
  border-radius: 10px;
  background: #f8fafc;
  overflow: hidden;
}

.wm-version-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 14px;
  border: 0;
  background: transparent;
  color: #475569;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all .2s;
}

.wm-version-btn i {
  font-size: 14px;
  color: #1a73e8;
}

.wm-version-btn:hover:not(:disabled) {
  background: #eff6ff;
  color: #1a73e8;
}

.wm-version-btn:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}

.wm-version-btn:disabled i {
  color: #cbd5e1;
}

.wm-version-sep {
  width: 1px;
  background: #e2e8f0;
}

.wm-download {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  border: 1px solid #1a73e8;
  border-radius: 10px;
  background: #1a73e8;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all .2s;
}

.wm-download i {
  font-size: 15px;
}

.wm-download:hover {
  border-color: #1557b0;
  background: #1557b0;
}

.wm-badge {
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 9px;
  background: #e8f1fe;
  color: #1a73e8;
  font-size: 11px;
  font-weight: 600;
  vertical-align: 1px;
}

/* 结果与预览样式已随「结果区/大图预览」一并移除；处理结果直接替换编辑区图片 */

@media (max-width: 900px) {
  .tool-page {
    padding: 14px 14px 32px;
  }

  .tool-heading h2 {
    font-size: 19px;
  }

  .tool-heading p {
    display: none;
  }

  .tool-card {
    padding: 16px 16px 28px;
  }

  .wm-actions {
    justify-content: stretch;
  }

  .wm-versions {
    flex: 1 1 100%;
  }

  .wm-version-btn {
    flex: 1;
    justify-content: center;
  }

  .wm-download {
    flex: 1 1 100%;
    justify-content: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/User2/Watermark.vue